import React, { useEffect, useState, useContext } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  ProgressBar,
  Button,
} from 'react-bootstrap'
import Cookies from 'js-cookie'
import { AuthContext } from '../AuthContext'
import axios from 'axios'
import { Helmet } from 'react-helmet'

// 計劃膠囊
const ProgramPill = ({ program }) => {
  // 為不同計劃設置不同顏色
  const colors = {
    代謝計畫: { bg: '#f3e5f5', color: '#4a148c', border: '#e1bee7' },
    腎臟: { bg: '#e3f2fd', color: '#0d47a1', border: '#bbdefb' },
    氣喘: { bg: '#e8f5e9', color: '#1b5e20', border: '#c8e6c9' },
    高血壓: { bg: '#fce4ec', color: '#880e4f', border: '#f8bbd0' },
    高血脂: { bg: '#f9fbe7', color: '#827717', border: '#f0f4c3' },
    糖尿病: { bg: '#fff3e0', color: '#e65100', border: '#ffe0b2' },
  }

  const style = colors[program] || {
    bg: '#f5f5f5',
    color: '#212121',
    border: '#e0e0e0',
  }

  return (
    <span
      style={{
        padding: '4px 12px',
        borderRadius: '20px',
        backgroundColor: style.bg,
        color: style.color,
        border: `1px solid ${style.border}`,
        display: 'inline-block',
        fontSize: '14px',
        marginRight: '4px',
        marginBottom: '4px',
      }}
    >
      {program}
    </span>
  )
}

// App 串接受狀態膠囊
const StatusPill = ({ status }) => {
  const style = {
    padding: '4px 12px',
    borderRadius: '20px',
    display: 'inline-block',
    fontSize: '14px',
  }

  if (status === '已串接') {
    return (
      <span
        style={{
          ...style,
          backgroundColor: '#e8f5e9',
          color: '#2e7d32',
          border: '1px solid #c8e6c9',
        }}
      >
        {status}
      </span>
    )
  }
  return (
    <span
      style={{
        ...style,
        backgroundColor: '#ffebee',
        color: '#c62828',
        border: '1px solid #ffcdd2',
      }}
    >
      {status}
    </span>
  )
}

const PatientDetail = () => {
  const { patient_id } = useParams()
  const navigate = useNavigate()
  const [patientData, setPatientData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const { baseUrl, currentUser, extendCookie, setLoginShow } =
    useContext(AuthContext)

  const getPatientInfoDetail = '/api/crm/project/patient/getPatientInfoDetail'
  const sessionFromCookie = Cookies.get('session')
  const { session = '' } = sessionFromCookie
    ? JSON.parse(sessionFromCookie)
    : {}

  useEffect(() => {
    const fetchPatientData = async () => {
      try {
        setIsLoading(true)

        if (!session) {
          setLoginShow(true)
          throw new Error('Session not found or expired')
        }
        const response = await axios.post(
          baseUrl + getPatientInfoDetail,
          { patient_id: patient_id },
          {
            headers: {
              'Content-Type': 'application/json',
              Accept: '*/*',
              session: currentUser?.session || session || '',
            },
          }
        )

        console.log('fetchPatientData', response)

        if (response.data.data) {
          extendCookie()
          setPatientData(response.data)
        } else if (response.data.error.code === '50004') {
          alert('登入已逾時，請重新登入')
          setLoginShow(true)
        }
      } catch (error) {
        console.error('Error fetching patient data:', error)
      } finally {
        setIsLoading(false)
      }
    }
    if (session) {
      fetchPatientData()
    } else {
      setLoginShow(true)
    }
  }, [patient_id, currentUser, session])

  const handleNavigateToDataCollection = () => {
    navigate(`/patient/${patient_id}/data-collection`)
  }

  const handleNavigateToDKABio = () => {
    navigate(`/patient/${patient_id}/dkabio-report`)
  }

  const handleGoBack = () => {
    navigate(-1)
  }

  if (isLoading) {
    return (
      <Container className="text-center">
        <div className="m-5">
          <ProgressBar className="m-4" animated now={100} />
          <h5 className="text-secondary">資料等待中...</h5>
        </div>
      </Container>
    )
  }

  if (error) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '80vh' }}
      >
        <p className="text-muted">無法載入病患資料</p>
      </Container>
    )
  }

  return (
    <>
      <Helmet>
        <title>用戶個人頁面 | HA! CRM</title>
      </Helmet>
      <Container className="py-4 ">
        <div className="mb-4">
          <Button
            variant="link"
            onClick={handleGoBack}
            className="text-decoration-none text-secondary"
          >
            <h5>
              <i className="fa-solid fa-arrow-left me-2"></i>
              返回用戶數據{' '}
            </h5>
          </Button>
        </div>
        {patientData && (
          <Row className="g-4 justify-content-center mt-5">
            {/* 左側個人資料卡片 */}
            <Col md={4}>
              <Card className="patient-info-card">
                <Card.Body className="my-3">
                  {/* 個人資料頭部 */}
                  <div className="mb-4 d-flex">
                    <div className="profile-image">
                      {patientData.icon ? (
                        <img
                          src={patientData.icon}
                          alt={patientData.name}
                          className="rounded-circle"
                        />
                      ) : (
                        <div className="default-avatar">
                          <i className="fa-solid fa-user" />
                        </div>
                      )}
                    </div>
                    <div className="d-flex flex-column justify-content-center">
                      <h4 className="mb-2">
                        {patientData.family_name + patientData.name}
                      </h4>
                      <h6>
                        {patientData.gender === 0 ? '女' : '男'} |{' '}
                        {patientData.birthday} (
                        {calculateAge(patientData.birthday)} 歲)
                      </h6>
                    </div>
                  </div>

                  {/* 個人資料列表 */}
                  <div className="info-list">
                    <InfoItem
                      icon="fa-solid fa-id-card"
                      label="身分證字號"
                      value={patientData.id_number}
                    />
                    <InfoItem
                      icon="fa-solid fa-phone"
                      label="電話"
                      value={patientData.phone}
                    />
                    <InfoItem
                      icon="fa-regular fa-calendar-plus"
                      label="建案日期"
                      value={patientData?.data[0].record_time}
                    />
                    <InfoItem
                      icon="fa-solid fa-mobile-screen"
                      label="App串接"
                      customContent={
                        <div style={{ marginTop: '4px' }}>
                          <StatusPill
                            status={
                              patientData.linked_ha_app === 1
                                ? '已串接'
                                : '未串接'
                            }
                          />
                        </div>
                      }
                    />
                    <InfoItem
                      icon="fa-solid fa-clipboard-list"
                      label="參加計劃"
                      customContent={
                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '4px',
                            marginTop: '4px',
                          }}
                        >
                          {patientData?.data.map((item, index) => (
                            <ProgramPill
                              key={`${item.project_name}-${index}`}
                              program={item.project_name}
                            />
                          ))}
                        </div>
                      }
                    />
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* 右側診療記錄卡片 */}
            <Col md={6}>
              <div className="function-buttons-container">
                <Button
                  className="function-button"
                  onClick={handleNavigateToDataCollection}
                >
                  <i className="fa-solid fa-database me-2"></i>
                  數據收集紀錄
                </Button>
                <Button
                  className="function-button"
                  onClick={handleNavigateToDKABio}
                >
                  <i className="fa-solid fa-file-medical me-2"></i>
                  DKABio 報告
                </Button>
              </div>
            </Col>
          </Row>
        )}
      </Container>{' '}
    </>
  )
}

const InfoItem = ({ icon, label, value, valueClassName, customContent }) => (
  <div className="info-item">
    <div className="info-icon">
      <i className={icon} />
    </div>
    <div className="info-content">
      <div className="info-label">{label}</div>
      {customContent ? (
        customContent
      ) : (
        <div className={`info-value ${valueClassName || ''}`}>
          {value || '-'}
        </div>
      )}
    </div>
  </div>
)

const StatusBadge = ({ status }) => {
  const statusConfig = {
    0: { label: '新收案', variant: 'primary' },
    1: { label: '追蹤管理', variant: 'success' },
    2: { label: '年度評估', variant: 'warning' },
    3: { label: '結案', variant: 'secondary' },
  }

  const config = statusConfig[status] || statusConfig[0]

  return (
    <Badge bg={config.variant} className="status-badge">
      {config.label}
    </Badge>
  )
}

const TrackTypeBadge = ({ trackType }) => {
  const trackTypeConfig = {
    0: { label: '初診', variant: 'info' },
    1: { label: '電話追蹤', variant: 'primary' },
    2: { label: '訪視', variant: 'warning' },
    3: { label: '回診', variant: 'success' },
  }

  const config = trackTypeConfig[trackType] || trackTypeConfig[0]

  return (
    <Badge bg={config.variant} className="track-type-badge">
      {config.label}
    </Badge>
  )
}

const calculateAge = (birthday) => {
  const birthDate = new Date(birthday)
  const today = new Date()
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--
  }

  return age
}

export default PatientDetail
